import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ClipboardCheck, ArrowLeft, Shield, FileText, Users, CreditCard, AlertTriangle, Mail } from 'lucide-react';

export default function TermsConditions() {
  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center mb-4">
            <ClipboardCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-foreground">QA Builder Pro</h1>
          <p className="text-muted-foreground mt-1">Terms & Conditions</p>
        </div>

        {/* Back Button */}
        <div className="mb-6">
          <Link to="/register">
            <Button variant="ghost" size="sm" data-testid="back-to-register-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Register
            </Button>
          </Link>
        </div>

        {/* Terms Content */}
        <Card className="border-slate-200 shadow-lg">
          <CardHeader>
            <CardTitle className="font-heading text-2xl">Terms and Conditions</CardTitle>
            <p className="text-sm text-muted-foreground">Last updated: December 2025</p>
          </CardHeader>
          <CardContent className="space-y-8">
            
            {/* Introduction */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">1. Introduction</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Welcome to QA Builder Pro, operated by <strong>QA Builder Pro</strong> ("Company", "we", "our", or "us"). QA Builder Pro is a construction quality assurance platform designed for builders, contractors, site managers, and trade professionals. These Terms and Conditions govern your use of our web application for creating, managing, and submitting QA inspection forms and checklists.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                By registering an account or using QA Builder Pro, you agree to comply with and be bound by these terms. If you do not agree, you must not use our services.
              </p>
            </section>

            {/* Service Description */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">2. Service Description</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                QA Builder Pro provides tools for construction quality assurance including:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Digital QA form creation and management for various construction trades</li>
                <li>Photo documentation with annotations and descriptions</li>
                <li>Multi-tier approval workflows (submission, review, approval, rejection)</li>
                <li>Company and site management with team collaboration</li>
                <li>PDF export with company branding and watermarks</li>
                <li>Offline capability for on-site use without internet connectivity</li>
              </ul>
            </section>

            {/* User Accounts & Roles */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">3. User Accounts & Roles</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                QA Builder Pro operates with a three-tier user system:
              </p>
              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div>
                  <span className="font-medium text-foreground">Admin:</span>
                  <span className="text-muted-foreground ml-2">Company owner with full control over users, forms, templates, and company settings.</span>
                </div>
                <div>
                  <span className="font-medium text-foreground">Manager:</span>
                  <span className="text-muted-foreground ml-2">Site managers who can review, approve, and manage forms. Requires admin approval to join.</span>
                </div>
                <div>
                  <span className="font-medium text-foreground">Operative:</span>
                  <span className="text-muted-foreground ml-2">Trade workers who create and submit QA forms for review.</span>
                </div>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                You are responsible for maintaining the security of your account credentials. You must not share your login details with others. Any activity under your account is your responsibility.
              </p>
            </section>

            {/* Acceptable Use */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">4. Acceptable Use</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                When using QA Builder Pro, you agree to:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Provide accurate and truthful information in all QA forms and inspections</li>
                <li>Only upload photos and documentation relevant to construction quality assurance</li>
                <li>Use the platform for legitimate construction QA purposes only</li>
                <li>Respect the intellectual property rights of others</li>
                <li>Not attempt to access accounts or data belonging to other companies</li>
                <li>Not upload malicious files, viruses, or harmful content</li>
                <li>Comply with all applicable construction industry regulations and standards</li>
              </ul>
            </section>

            {/* Data & Documentation */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">5. Data & Documentation</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                All QA forms, photos, and documentation you create remain your property. You grant QA Builder Pro a limited license to store, process, and display this content solely for providing our services to you.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                <strong>Important:</strong> QA Builder Pro is a documentation tool. The accuracy of inspection data, compliance with building codes, and quality assurance decisions remain the responsibility of qualified professionals within your organisation. Our platform does not provide construction advice or certification.
              </p>
            </section>

            {/* Subscription & Payments */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">6. Subscription & Payments</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                QA Builder Pro offers both free and paid subscription tiers. Paid features include unlimited forms, advanced templates, priority support, and additional storage.
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Subscription fees are billed monthly or annually as selected</li>
                <li>Payments are processed securely through our payment provider</li>
                <li>You may cancel your subscription at any time from your account settings</li>
                <li>Refunds are handled according to our Refund Policy</li>
                <li>We reserve the right to modify pricing with 30 days notice</li>
              </ul>
            </section>

            {/* Limitation of Liability */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <h3 className="text-lg font-semibold text-foreground">7. Limitation of Liability</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                QA Builder Pro is provided "as is" without warranties of any kind. We do not guarantee uninterrupted access or that the service will be error-free.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                To the maximum extent permitted by law, QA Builder Pro shall not be liable for:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Any construction defects, safety issues, or compliance failures</li>
                <li>Decisions made based on QA forms created using our platform</li>
                <li>Loss of data due to technical failures or user error</li>
                <li>Indirect, incidental, or consequential damages</li>
                <li>Any losses exceeding the fees paid in the 12 months prior</li>
              </ul>
            </section>

            {/* Modifications */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">8. Changes to Terms</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                We may update these Terms and Conditions from time to time. Material changes will be communicated via email or in-app notification. Continued use of QA Builder Pro after changes take effect constitutes acceptance of the revised terms.
              </p>
            </section>

            {/* Contact */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">9. Contact Us</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                If you have questions about these Terms and Conditions or need support, please contact us through the application or reach out to your company administrator.
              </p>
            </section>

            {/* Agreement */}
            <div className="border-t pt-6 mt-8">
              <p className="text-sm text-muted-foreground text-center">
                By creating an account, you confirm that you have read, understood, and agree to these Terms and Conditions.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer Links */}
        <div className="mt-8 text-center space-x-4">
          <Link to="/register" className="text-primary hover:underline text-sm font-medium">
            Create Account
          </Link>
          <span className="text-muted-foreground">|</span>
          <Link to="/login" className="text-primary hover:underline text-sm font-medium">
            Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
